/**
 * Pipeline orchestrator.
 *
 * Composes the 7-layer flow:
 *
 *   CustomerInput
 *     → normalize()
 *     → CoreRuleEngine  (+ ReferenceData)
 *     → AnalysisEngine  (+ ReferenceData)
 *     → InterpretationEngine  (+ ReferenceData)
 *     → ReportViewModel
 *     → BlueprintResult  (consumed by Dashboard / Report UI)
 *
 * Two profiles are provided:
 *   - POC-MOCK : all-mock layers (current behavior, deterministic).
 *   - V2.9.9   : all layers throw NOT_IMPLEMENTED. Reserved for Golden Master.
 *
 * Mixed profiles are intentionally NOT supported yet — a run is either fully
 * mocked or fully V2.9.9. This keeps `sourceStatus` unambiguous during the
 * migration window.
 */
import { ENGINE_VERSION } from "./sourceStatus";
import { referenceData } from "./referenceData";
import { runCoreRuleEngineMock, runCoreRuleEngineV299 } from "./coreRuleEngine";
import { runAnalysisEngineMock, runAnalysisEngineV299 } from "./analysisEngine";
import {
  runInterpretationEngineMock,
  runInterpretationEngineV299,
} from "./interpretationEngine";
import { buildReportViewModel } from "./reportViewModel";
import { normalizeInput } from "./normalize";

const PROFILES = {
  [ENGINE_VERSION.POC_MOCK]: {
    core: runCoreRuleEngineMock,
    analysis: runAnalysisEngineMock,
    interpretation: runInterpretationEngineMock,
  },
  [ENGINE_VERSION.V299]: {
    core: runCoreRuleEngineV299,
    analysis: runAnalysisEngineV299,
    interpretation: runInterpretationEngineV299,
  },
};

function resolveEngineVersion(explicit) {
  if (explicit && PROFILES[explicit]) return explicit;
  const fromEnv =
    typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_LIFE_BLUEPRINT_ENGINE;
  if (fromEnv && PROFILES[fromEnv]) return fromEnv;
  return ENGINE_VERSION.POC_MOCK;
}

/**
 * Run the full pipeline and return per-layer outputs plus the final
 * BlueprintResult. Useful for the Golden Master validation harness so it can
 * diff any layer, not just the final view.
 *
 * @param {import('./schema').RawCustomerInput} raw
 * @param {{ engineVersion?: string }} [options]
 * @returns {{
 *   engineVersion: string,
 *   normalized: import('./schema').NormalizedInput,
 *   core: import('./coreRuleEngine').CoreOutput,
 *   analysis: import('./analysisEngine').AnalysisOutput,
 *   interpretation: import('./interpretationEngine').InterpretationOutput,
 *   report: import('./schema').BlueprintResult
 * }}
 */
export function runPipeline(raw, options = {}) {
  const engineVersion = resolveEngineVersion(options.engineVersion);
  const profile = PROFILES[engineVersion];

  const normalized = normalizeInput(raw);
  const core = profile.core(normalized, referenceData);
  const analysis = profile.analysis(core, referenceData);
  const interpretation = profile.interpretation(analysis, referenceData);
  const report = buildReportViewModel(core, analysis, interpretation, normalized, raw);

  return { engineVersion, normalized, core, analysis, interpretation, report };
}

/**
 * Convenience wrapper: returns only the final BlueprintResult for the UI.
 * @param {import('./schema').RawCustomerInput} raw
 * @param {{ engineVersion?: string }} [options]
 * @returns {import('./schema').BlueprintResult}
 */
export function generateBlueprint(raw, options = {}) {
  return runPipeline(raw, options).report;
}
